import React, { useEffect, useState, useRef } from 'react';
import { ScrollRestoration } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import PageHero from './PageHero';
import AnimateOnScroll from './AnimateOnScroll';
import Lightbox from './Lightbox';
import stationFront from '../resources/IMG_6688.jpeg';
import stationSide from '../resources/IMG_9606.jpeg';
import gallery1 from '../resources/IMG_6685.jpeg';
import gallery2 from '../resources/IMG_6686.jpeg';
import gallery3 from '../resources/IMG_9667.jpeg';
import gallery4 from '../resources/IMG_9672.jpeg';
import gallery5 from '../resources/IMG_9674.jpeg';
import gallery6 from '../resources/IMG_9677.jpeg';
import 'animate.css';

// Poradie v galérii: najprv 2 hero fotky (ako sú v koláži), potom ostatné
const GALLERY_IMAGES = [
    stationSide,   // index 0 = veľká fotka v koláži (IMG_9606)
    stationFront,  // index 1 = malá fotka v koláži (IMG_6688)
    gallery1, gallery2, gallery3, gallery4, gallery5, gallery6,
];

const FALLBACK_PRICES = [
    { name: 'Natural 95', price: '1.599', icon: '⛽' },
    { name: 'Diesel', price: '1.549', icon: '🛢️' },
    { name: 'Natural 98', price: '1.699', icon: '⚡' },
];

const SERVICES = [
    { icon: '⛽', title: 'Nafta & Benzín', desc: 'Slovnaft palivá najvyššej kvality.' },
    { icon: '💧', title: 'AdBlue', desc: 'Pre dieselové vozidlá s technológiou SCR.' },
    { icon: '💨', title: 'Stlačený vzduch', desc: 'Dohuštenie pneumatík zadarmo.' },
    { icon: '💳', title: 'Platba kartou', desc: 'Akceptujeme všetky platobné karty.' },
    { icon: '📅', title: 'Otvorené každý deň', desc: 'Sme k dispozícii 7 dní v týždni.' },
    { icon: '🚛', title: 'Veľkokapacitné čerpanie', desc: 'Vhodné pre nákladné vozidlá a techniku.' },
];

const HOURS = [
    { day: 'Pondelok – Nedela', time: '06:00 – 22:00' },
];

function useCountUp(target, duration = 1200, active = false) {
    const [display, setDisplay] = useState('0.000');
    useEffect(() => {
        if (!active) return;
        const num = parseFloat(target);
        if (isNaN(num)) { setDisplay(target); return; }
        const steps = 40;
        const interval = duration / steps;
        let step = 0;
        const timer = setInterval(() => {
            step++;
            const val = (num * step) / steps;
            setDisplay(val.toFixed(3));
            if (step >= steps) { clearInterval(timer); setDisplay(num.toFixed(3)); }
        }, interval);
        return () => clearInterval(timer);
    }, [target, duration, active]);
    return display;
}

function PriceCard({ icon, name, price, isFallback, active }) {
    const displayPrice = useCountUp(price, 1200, active);
    return (
        <div className="flex flex-col items-center bg-gray-800 rounded-2xl px-8 py-8 shadow-xl border border-gray-700 min-w-[180px]">
            <span className="text-4xl mb-3">{icon}</span>
            <span className="text-gray-400 text-sm font-medium uppercase tracking-widest mb-2">{name}</span>
            <span className="text-white font-extrabold text-4xl tracking-tight">
                <span className="text-2xl font-semibold text-gray-400 align-top mr-1">~</span>
                {displayPrice} <span className="text-xl font-semibold text-gray-300">€/l</span>
            </span>
            <span className="mt-3 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full">Slovnaft</span>
            <span className="mt-2 text-gray-500 text-xs italic">približná cena</span>
        </div>
    );
}

function CerpaciaStanica() {
    const [prices, setPrices] = useState(null);
    const [isFallback, setIsFallback] = useState(false);
    const [updatedDate, setUpdatedDate] = useState(null);
    const [pricesVisible, setPricesVisible] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(null); // null = zatvorený
    const pricesSectionRef = useRef(null);

    const openLightbox = (i) => setLightboxIndex(i);
    const closeLightbox = () => setLightboxIndex(null);

    useEffect(() => {
        // Snaha v poradí: /prices.php (scrapuje benzin.sk a cache-uje 24h)
        //                → /prices.json (manuálny override / dev fallback)
        //                → hardcoded FALLBACK_PRICES
        // Cache-bust query, aby update bol vidno okamžite.
        const tryFetch = async (url) => {
            const r = await fetch(`${url}?t=${Date.now()}`);
            if (!r.ok) throw new Error('not ok');
            const data = await r.json();
            if (!Array.isArray(data?.prices) || data.prices.length === 0) {
                throw new Error('invalid');
            }
            return data;
        };

        (async () => {
            for (const url of ['/prices.php', '/prices.json']) {
                try {
                    const data = await tryFetch(url);
                    setPrices(data.prices);
                    if (data.source === 'fallback') setIsFallback(true);
                    if (data.updated) {
                        const d = new Date(data.updated);
                        if (!isNaN(d.getTime())) {
                            setUpdatedDate(
                                d.toLocaleDateString('sk-SK', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                })
                            );
                        }
                    }
                    return;
                } catch {
                    // skús ďalšie url
                }
            }
            // všetko zlyhalo
            setPrices(FALLBACK_PRICES);
            setIsFallback(true);
        })();
    }, []);

    useEffect(() => {
        if (!pricesSectionRef.current) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setPricesVisible(true); },
            { threshold: 0.2 }
        );
        observer.observe(pricesSectionRef.current);
        return () => observer.disconnect();
    }, []);

    const scrollToMap = () => {
        document.getElementById('mapa-stanica')?.scrollIntoView({ behavior: 'smooth' });
    };

    const displayPrices = prices ?? FALLBACK_PRICES;

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />
            <PageHero
                title="Čerpacia"
                highlight="stanica"
                subtitle="Tankujte u nás – Slovnaft palivá, rýchlo a spoľahlivo."
            />

            {/* Fuel prices band */}
            <section ref={pricesSectionRef} className="bg-gray-900 py-14 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <h2 className="text-center text-white font-bold text-3xl mb-2">
                        Približné ceny palív
                    </h2>
                    <p className="text-center text-gray-400 text-sm mb-2">
                        Toto sú <strong className="text-white">približné</strong> ceny — týždenný priemer PHM v SR.
                        Presná cena na našom stojane v Giraltovciach sa môže mierne líšiť, overte si ju priamo na stanici.
                    </p>
                    <p className="text-center text-gray-500 text-xs mb-10">
                        {updatedDate && !isFallback && (
                            <>Aktualizované: {updatedDate} · </>
                        )}
                        Zdroj: benzin.sk
                    </p>
                    <div className="flex flex-wrap justify-center gap-6">
                        {displayPrices.map((p, i) => (
                            <PriceCard key={i} {...p} isFallback={isFallback} active={pricesVisible} />
                        ))}
                    </div>
                    {isFallback && (
                        <p className="text-center text-gray-600 text-xs mt-6">
                            Ceny sú orientačné – aktuálne ceny overte priamo na čerpacej stanici.
                        </p>
                    )}
                </div>
            </section>

            {/* About section */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-screen-xl mx-auto flex flex-col lg:flex-row gap-12 items-center">
                    <AnimateOnScroll className="flex-1">
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">
                            Čerpacia stanica <span className="text-red-600">Petromin</span>
                        </h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Naša čerpacia stanica v Giraltovciach je partnerskou stanicou siete{' '}
                            <strong>Slovnaft</strong>. Ponúkame kvalitné palivá pre osobné aj nákladné
                            vozidlá, stavebnú a poľnohospodársku techniku.
                        </p>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Okrem palív nájdete u nás AdBlue, stlačený vzduch na dohuštenie pneumatík
                            a rýchlu obsluhu bez čakania. Stanica je otvorená každý deň.
                        </p>
                        <p className="text-gray-600 leading-relaxed mb-6">
                            Sídlime priamo v areáli Petromin s.r.o. — môžete prísť natankovať a zároveň
                            si objednať stavebný materiál alebo dopravu.
                        </p>
                        <button
                            onClick={scrollToMap}
                            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-3 rounded transition-colors duration-200"
                        >
                            Zobraziť na mape
                        </button>
                    </AnimateOnScroll>
                    <AnimateOnScroll className="flex-1 w-full" delay={150}>
                        <div className="relative h-[420px] sm:h-[460px] w-full">
                            {/* Veľká fotka (vľavo hore) */}
                            <button
                                type="button"
                                onClick={() => openLightbox(0)}
                                className="group absolute top-0 left-0 w-[78%] h-[72%] rounded-2xl shadow-2xl overflow-hidden cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-red-500/50"
                                aria-label="Otvoriť galériu — bočný pohľad"
                            >
                                <img
                                    src={stationSide}
                                    alt="Čerpacia stanica Petromin – bočný pohľad"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-xl">
                                        <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                        </svg>
                                    </div>
                                </div>
                            </button>

                            {/* Malá fotka (vpravo dole) */}
                            <button
                                type="button"
                                onClick={() => openLightbox(1)}
                                className="group absolute bottom-0 right-0 w-[68%] h-[60%] rounded-2xl shadow-2xl overflow-hidden border-4 border-white cursor-zoom-in focus:outline-none focus:ring-4 focus:ring-red-500/50"
                                aria-label="Otvoriť galériu — pohľad spredu"
                            >
                                <img
                                    src={stationFront}
                                    alt="Čerpacia stanica Petromin – pohľad spredu"
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3 shadow-xl">
                                        <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                                        </svg>
                                    </div>
                                </div>
                            </button>

                            {/* "Pozrieť všetky fotky" badge */}
                            <button
                                type="button"
                                onClick={() => openLightbox(0)}
                                className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/95 hover:bg-white text-gray-800 text-xs font-semibold px-3 py-2 rounded-full shadow-lg flex items-center gap-1.5 transition-all duration-200 hover:scale-105 z-10"
                            >
                                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {GALLERY_IMAGES.length} fotiek
                            </button>

                            <div className="absolute -top-3 -left-3 w-20 h-20 bg-red-600 rounded-2xl -z-10 hidden lg:block"></div>
                            <div className="absolute -bottom-3 -right-3 w-24 h-24 border-4 border-red-600 rounded-2xl -z-10 hidden lg:block"></div>
                        </div>
                    </AnimateOnScroll>
                </div>
            </section>

            {/* Services section */}
            <section className="bg-gray-50 py-20 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-2">Naše služby</h2>
                        <p className="text-center text-gray-500 mb-12 text-sm">
                            Všetko čo potrebujete na jednom mieste.
                        </p>
                    </AnimateOnScroll>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {SERVICES.map((s, i) => (
                            <AnimateOnScroll key={i} delay={i * 80}>
                                <div className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-start gap-4">
                                    <span className="text-3xl mt-1">{s.icon}</span>
                                    <div>
                                        <h3 className="font-bold text-gray-800 text-lg mb-1">{s.title}</h3>
                                        <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                                    </div>
                                </div>
                            </AnimateOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            {/* Opening hours */}
            <section className="bg-white py-20 px-6">
                <div className="max-w-md mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-10">Otváracie hodiny</h2>
                        <div className="rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                            <div className="bg-red-600 text-white grid grid-cols-2 px-6 py-3 font-semibold text-sm uppercase tracking-wider">
                                <span>Deň</span>
                                <span className="text-right">Hodiny</span>
                            </div>
                            {HOURS.map((h, i) => (
                                <div
                                    key={i}
                                    className={`grid grid-cols-2 px-6 py-4 text-sm ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                                >
                                    <span className="font-medium text-gray-700">{h.day}</span>
                                    <span className="text-right font-bold text-gray-900">{h.time}</span>
                                </div>
                            ))}
                        </div>
                    </AnimateOnScroll>
                </div>
            </section>

            {/* Map section */}
            <section id="mapa-stanica" className="bg-gray-50 py-20 px-6">
                <div className="max-w-screen-xl mx-auto">
                    <AnimateOnScroll>
                        <h2 className="text-center font-bold text-4xl text-gray-800 mb-10">Kde nás nájdete</h2>
                    </AnimateOnScroll>
                    <AnimateOnScroll delay={100}>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1305.9504909034154!2d21.505501604674382!3d49.107521521468314!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473e97030911bc1b%3A0xb3a5606c0042e89b!2sPETROMIN%2C%20spol.%20s.r.o.!5e0!3m2!1ssk!2ssk!4v1717443646955!5m2!1ssk!2ssk"
                            width="100%"
                            height="450"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="rounded-2xl shadow-2xl w-full"
                            title="Mapa čerpacej stanice Petromin"
                        />
                    </AnimateOnScroll>
                </div>
            </section>

            <Footer />
            <ScrollRestoration />

            {/* Lightbox / galéria */}
            {lightboxIndex !== null && (
                <Lightbox
                    images={GALLERY_IMAGES}
                    startIndex={lightboxIndex}
                    onClose={closeLightbox}
                />
            )}
        </div>
    );
}

export default CerpaciaStanica;
